
import React, { useRef, useEffect } from 'react';
import { Editor } from '@toast-ui/react-editor';
import '@toast-ui/editor/dist/toastui-editor.css';
import { useLanguage } from '../contexts/LanguageContext';

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  height?: string;
}

export const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  placeholder,
  height = "300px"
}) => {
  const { t } = useLanguage();
  const defaultPlaceholder = placeholder || t('editor.placeholder');
  const editorRef = useRef<Editor>(null);

  useEffect(() => {
    if (editorRef.current && value !== editorRef.current.getInstance().getMarkdown()) {
      editorRef.current.getInstance().setMarkdown(value);
    }
  }, [value]);

  const handleChange = () => {
    if (editorRef.current) {
      const markdown = editorRef.current.getInstance().getMarkdown();
      onChange(markdown);
    }
  };

  useEffect(() => {
    // Apply custom styles for better readability in dark theme
    const styleElement = document.createElement('style');
    styleElement.textContent = `
      /* Container principal avec style moderne et lisible */
      .rich-text-editor .toastui-editor-defaultUI {
        border: 2px solid rgb(124, 58, 237);
        border-radius: 12px;
        background-color: #ffffff;
        box-shadow: 0 4px 20px rgba(124, 58, 237, 0.2);
        overflow: hidden;
      }
      
      /* Toolbar moderne et contraste élevé */
      .rich-text-editor .toastui-editor-toolbar {
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        border-bottom: none;
        padding: 12px 16px;
        box-shadow: 0 2px 8px rgba(0,0,0,0.1);
      }
      
      /* Boutons toolbar avec excellent contraste */
      .rich-text-editor .toastui-editor-toolbar button {
        color: white !important;
        background-color: rgba(255, 255, 255, 0.15);
        border: 1px solid rgba(255, 255, 255, 0.3);
        border-radius: 8px;
        margin: 0 4px;
        padding: 8px 12px;
        font-family: 'Segoe UI', sans-serif;
        font-size: 13px;
        font-weight: 500;
        transition: all 0.3s ease;
        backdrop-filter: blur(10px);
      }
      
      .rich-text-editor .toastui-editor-toolbar button:hover {
        background-color: rgba(255, 255, 255, 0.25);
        border-color: rgba(255, 255, 255, 0.5);
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(0,0,0,0.2);
      }
      
      .rich-text-editor .toastui-editor-toolbar button.active {
        background-color: rgba(255, 255, 255, 0.9);
        color: #764ba2 !important;
        box-shadow: 0 0 15px rgba(255, 255, 255, 0.4);
      }
      
      /* Zone d'édition avec excellent contraste */
      .rich-text-editor .toastui-editor-md-container .toastui-editor-md {
        background-color: #ffffff !important;
        color: #2d3748 !important;
        font-family: 'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
        font-size: 15px;
        line-height: 1.7;
        padding: 24px;
        min-height: 280px;
      }
      
      /* Curseur et sélection bien visibles */
      .rich-text-editor .toastui-editor-md-container .toastui-editor-md .ProseMirror {
        background-color: #ffffff !important;
        color: #2d3748 !important;
        caret-color: #764ba2;
      }
      
      .rich-text-editor .toastui-editor-md-container .toastui-editor-md .ProseMirror::selection {
        background-color: rgba(118, 75, 162, 0.2);
      }
      
      /* Placeholder bien visible */
      .rich-text-editor .toastui-editor-md-container .toastui-editor-md .ProseMirror p.is-empty:first-child::before {
        color: #a0aec0 !important;
        font-style: italic;
        font-size: 15px;
      }
      
      /* Preview avec style moderne */
      .rich-text-editor .toastui-editor-preview {
        background-color: #f7fafc !important;
        color: #2d3748 !important;
        padding: 24px;
        font-family: 'Inter', sans-serif;
        line-height: 1.7;
      }
      
      /* Titres dans le preview */
      .rich-text-editor .toastui-editor-preview h1,
      .rich-text-editor .toastui-editor-preview h2,
      .rich-text-editor .toastui-editor-preview h3 {
        color: #764ba2 !important;
        font-weight: 600;
      }
      
      /* Dropdowns modernes */
      .rich-text-editor .toastui-editor-dropdown-toolbar {
        background-color: #1e293b !important;
        border: 2px solid rgb(124, 58, 237) !important;
        border-radius: 12px;
        box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
        backdrop-filter: blur(20px);
        z-index: 9999 !important;
      }
      
      .rich-text-editor .toastui-editor-dropdown-toolbar button {
        color: #f8fafc !important;
        background-color: transparent !important;
        border: none !important;
        padding: 12px 16px !important;
        margin: 4px !important;
        border-radius: 8px !important;
        font-family: 'Inter', sans-serif !important;
        font-weight: 500 !important;
        transition: all 0.2s ease !important;
      }
      
      .rich-text-editor .toastui-editor-dropdown-toolbar button:hover {
        background-color: rgba(255, 255, 255, 0.1) !important;
        color: #ffffff !important;
      }
      
      /* Headers dropdown */
      .toastui-editor-popup-add-heading .toastui-editor-dropdown-toolbar {
        background-color: #1e293b !important;
        border: 2px solid rgb(124, 58, 237) !important;
        border-radius: 12px;
        z-index: 9999 !important;
      }
      
      .toastui-editor-popup-add-heading button {
        color: #f8fafc !important;
        background-color: transparent !important;
        font-family: 'Inter', sans-serif !important;
        font-weight: 500 !important;
      }
      
      .toastui-editor-popup-add-heading button:hover {
        background-color: rgba(255, 255, 255, 0.1) !important;
        color: #ffffff !important;
      }
      
      /* Scrollbar moderne */
      .rich-text-editor .toastui-editor-md-container .toastui-editor-md::-webkit-scrollbar {
        width: 8px;
      }
      
      .rich-text-editor .toastui-editor-md-container .toastui-editor-md::-webkit-scrollbar-track {
        background: #e2e8f0;
        border-radius: 4px;
      }
      
      .rich-text-editor .toastui-editor-md-container .toastui-editor-md::-webkit-scrollbar-thumb {
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        border-radius: 4px;
      }
      
      .rich-text-editor .toastui-editor-md-container .toastui-editor-md::-webkit-scrollbar-thumb:hover {
        background: linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%);
      }
      
      /* Tabs modernes */
      .rich-text-editor .toastui-editor-mode-switch {
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        border: none;
        padding: 0 16px;
      }
      
      .rich-text-editor .toastui-editor-mode-switch .tab-item {
        color: rgba(255, 255, 255, 0.8) !important;
        background-color: transparent;
        border: none;
        font-family: 'Inter', sans-serif;
        font-weight: 500;
        margin: 0 8px;
        padding: 12px 20px;
        border-radius: 8px 8px 0 0;
        transition: all 0.3s ease;
      }
      
      .rich-text-editor .toastui-editor-mode-switch .tab-item:hover {
        color: white !important;
        background-color: rgba(255, 255, 255, 0.1);
      }
      
      .rich-text-editor .toastui-editor-mode-switch .tab-item.active {
        background-color: rgba(255, 255, 255, 0.2);
        color: white !important;
        box-shadow: 0 0 20px rgba(255, 255, 255, 0.3);
      }
      
      /* Focus states pour accessibilité */
      .rich-text-editor .toastui-editor-toolbar button:focus,
      .rich-text-editor .toastui-editor-mode-switch .tab-item:focus {
        outline: 2px solid rgba(255, 255, 255, 0.5);
        outline-offset: 2px;
      }

      /* Popups & dropdowns - sombre, lisible, z-index élevé */
      .rich-text-editor .toastui-editor-popup {
        background-color: #1e293b !important;
        border: 2px solid rgb(124, 58, 237) !important;
        border-radius: 12px !important;
        box-shadow: 0 12px 24px rgba(0,0,0,0.3) !important;
        z-index: 10000 !important;
      }
      .rich-text-editor .toastui-editor-dropdown-toolbar,
      .rich-text-editor .toastui-editor-dropdown-toolbar ul,
      .rich-text-editor .toastui-editor-dropdown-toolbar li,
      .rich-text-editor .toastui-editor-dropdown-toolbar a,
      .rich-text-editor .toastui-editor-dropdown-toolbar button {
        background-color: transparent !important;
        color: #f1f5f9 !important; /* texte clair */
      }
      .rich-text-editor .toastui-editor-dropdown-toolbar li:hover,
      .rich-text-editor .toastui-editor-dropdown-toolbar a:hover,
      .rich-text-editor .toastui-editor-dropdown-toolbar button:hover {
        background-color: rgba(255,255,255,0.08) !important;
        color: #ffffff !important;
      }
      .rich-text-editor .toastui-editor-popup-add-heading .toastui-editor-dropdown-toolbar {
        background-color: #1e293b !important;
      }
      .rich-text-editor .toastui-editor-popup-add-heading .toastui-editor-dropdown-toolbar ul,
      .rich-text-editor .toastui-editor-popup-add-heading .toastui-editor-dropdown-toolbar li,
      .rich-text-editor .toastui-editor-popup-add-heading .toastui-editor-dropdown-toolbar a,
      .rich-text-editor .toastui-editor-popup-add-heading .toastui-editor-dropdown-toolbar button {
        color: #f1f5f9 !important;
      }
      .rich-text-editor .toastui-editor-toolbar-items .tui-toolbar-item .tui-toolbar-icons {
        color: #ffffff !important;
      }
    `;
    document.head.appendChild(styleElement);

    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);

  return (
    <div className="rich-text-editor">
      <Editor
        ref={editorRef}
        initialValue={value}
        previewStyle="tab"
        height={height}
        initialEditType="markdown"
        useCommandShortcut={true}
        onChange={handleChange}
        hideModeSwitch={false}
        toolbarItems={[
          ['heading', 'bold', 'italic', 'strike'],
          ['hr', 'quote'],
          ['ul', 'ol', 'task', 'indent', 'outdent'],
          ['table', 'link'],
          ['code', 'codeblock']
        ]}
        placeholder={defaultPlaceholder}
        theme="dark"
      />
    </div>
  );
};
